import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import HelpIcon from "@redotech/redo-icons/help-circle.svg";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { Treatment } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import {
  AutomationOutreachSplitMetricsResponse,
  VariantMetrics,
} from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split-metrics";
import { AutomationOutreachStepType } from "@redotech/redo-model/split";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import * as redoMetricItemCss from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item.module.css";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { ButtonSize, IconButton } from "@redotech/redo-web/button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import DownArrowIcon from "@redotech/redo-web/icon-old/down-arrow.svg";
import UpArrowIcon from "@redotech/redo-web/icon-old/up-arrow.svg";
import {
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import {
  RedoMetricTooltip,
  RedoMetricTooltipProps,
} from "@redotech/redo-web/tooltip/metric-tooltip";
import { memo, ReactNode, useMemo, useState } from "react";
import { VariantPlaceholder } from "./common/variant-placeholders";

export interface AutomationAbTestResultsPanelProps {
  automationId: string;
  automationStepId: string;
  stepType: AutomationOutreachStepType;
  title?: string;
  icon?: ReactNode;
  onSelectWinner?: (winningTemplateId: string) => void;
  renderVariantPreview?: (treatment: Treatment) => ReactNode;
  renderFullVariantPreview?: (treatment: Treatment) => ReactNode;
  onViewVariant?: (treatment: Treatment) => void;
}

interface MetricRowProps {
  variantName: string;
  value: string;
  isWinner: boolean;
}

const MetricRow = memo(function MetricRow({
  variantName,
  value,
  isWinner,
}: MetricRowProps) {
  return (
    <Flex align="center" justify="space-between" w="full">
      <Text fontSize="sm" textColor="secondary">
        {variantName}
      </Text>
      <Text
        fontSize="sm"
        fontWeight={isWinner ? "bold" : "regular"}
        textColor="primary"
      >
        {value}
      </Text>
    </Flex>
  );
});

interface MetricVariant {
  name: string;
  value: number;
  formattedValue: string;
}

interface DualViewProps {
  primaryTitle?: string;
  primaryTooltipProps?: RedoMetricTooltipProps;
  secondaryTitle?: string;
  secondaryTooltipProps?: RedoMetricTooltipProps;
}

interface MetricCardProps extends DualViewProps {
  primaryVariants: MetricVariant[];
  secondaryVariants?: MetricVariant[];
  higherIsBetter?: boolean;
  comparisonText?: string;
  primaryPercentChange?: number;
  secondaryPercentChange?: number;
}

const MetricCard = memo(function MetricCard({
  primaryTitle,
  secondaryTitle,
  primaryVariants,
  secondaryVariants,
  higherIsBetter = true,
  primaryTooltipProps,
  secondaryTooltipProps,
  comparisonText,
  primaryPercentChange,
  secondaryPercentChange,
}: MetricCardProps) {
  const [showSecondary, setShowSecondary] = useState(false);

  const variants =
    showSecondary && secondaryVariants ? secondaryVariants : primaryVariants;

  const title =
    showSecondary && (secondaryTooltipProps?.title || secondaryTitle)
      ? secondaryTooltipProps?.title || secondaryTitle
      : primaryTooltipProps?.title || primaryTitle;
  const tooltipProps =
    showSecondary && secondaryTooltipProps
      ? secondaryTooltipProps
      : primaryTooltipProps;

  const winningValue = useMemo(() => {
    const values = variants.map((v) => v.value).filter((v) => v > 0);
    if (values.length === 0) {
      return null;
    }
    return higherIsBetter ? Math.max(...values) : Math.min(...values);
  }, [variants, higherIsBetter]);

  const percentChange = showSecondary
    ? secondaryPercentChange
    : primaryPercentChange;
  const showPercentChange = percentChange !== undefined;
  const computedIsGoodChange = showSecondary
    ? secondaryPercentChange !== undefined
      ? secondaryPercentChange >= 0
      : undefined
    : primaryPercentChange !== undefined
      ? primaryPercentChange >= 0
      : undefined;

  return (
    <Card p="lg" radius="sm">
      <Flex dir="column" gap="md" w="full">
        <Flex align="center" justify="space-between" w="full">
          <Flex align="center" gap="xs">
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              {title}
            </Text>
            {tooltipProps && (
              <RedoMetricTooltip {...tooltipProps}>
                <IconButton size={ButtonSize.SMALL}>
                  <HelpIcon
                    style={{
                      width: "14px",
                      height: "14px",
                      color: "var(--redo-colors-foreground-fg-quinary-400)",
                    }}
                  />
                </IconButton>
              </RedoMetricTooltip>
            )}
          </Flex>
          {secondaryVariants && (
            <RedoButton
              hierarchy="tertiary"
              onClick={() => setShowSecondary((prev) => !prev)}
              size="xs"
              text="Toggle view"
            />
          )}
        </Flex>
        <Flex dir="column" gap="sm" w="full">
          {variants.map((variant) => (
            <MetricRow
              isWinner={winningValue !== null && variant.value === winningValue}
              key={variant.name}
              value={variant.formattedValue}
              variantName={variant.name}
            />
          ))}
        </Flex>
        {showPercentChange && (
          <Flex align="center" gap="xs" justify="flex-end">
            <Flex
              align="center"
              className={
                computedIsGoodChange
                  ? redoMetricItemCss.green
                  : redoMetricItemCss.red
              }
              gap="xxs"
            >
              {percentChange > 0 ? (
                <UpArrowIcon className={redoMetricItemCss.changeIcon} />
              ) : percentChange < 0 ? (
                <DownArrowIcon className={redoMetricItemCss.changeIcon} />
              ) : (
                <MinusIcon className={redoMetricItemCss.changeIcon} />
              )}
              <Text fontSize="xs">{percentChange.toFixed(2)}%</Text>
            </Flex>
            {comparisonText && (
              <Text color="tertiary" fontSize="xs">
                {comparisonText}
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  );
});

function formatPercentage(numerator: number, denominator: number): string {
  if (denominator === 0) {
    return "0%";
  }
  return `${((numerator / denominator) * 100).toFixed(2)}%`;
}

function formatCurrency(dollars: number): string {
  return `$${dollars.toFixed(2)}`;
}

/**
 * Creates metric variant data for count-based metrics.
 */
function createCountMetric<T extends { treatmentName: string }>(
  variants: T[],
  getValue: (v: T) => number,
): MetricVariant[] {
  return variants.map((v) => {
    const value = getValue(v);
    return {
      name: v.treatmentName,
      value,
      formattedValue: value > 0 ? value.toLocaleString() : "-",
    };
  });
}

/**
 * Creates metric variant data for currency-based metrics.
 */
function createCurrencyMetric<T extends { treatmentName: string }>(
  variants: T[],
  getValue: (v: T) => number,
): MetricVariant[] {
  return variants.map((v) => {
    const value = getValue(v);
    return {
      name: v.treatmentName,
      value,
      formattedValue: value > 0 ? formatCurrency(value) : "-",
    };
  });
}

/**
 * Creates metric variant data for rate-based metrics (percentage of two values).
 */
function createRateMetric<T extends { treatmentName: string }>(
  variants: T[],
  getNumerator: (v: T) => number,
  getDenominator: (v: T) => number,
): MetricVariant[] {
  return variants.map((v) => {
    const numerator = getNumerator(v);
    const denominator = getDenominator(v);
    const value = denominator > 0 ? numerator / denominator : 0;
    return {
      name: v.treatmentName,
      value,
      formattedValue:
        denominator > 0 ? formatPercentage(numerator, denominator) : "-",
    };
  });
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface VariantRowData {
  variant: VariantMetrics;
  trafficPercent: string;
  opens: number;
  clicks: number;
  revenue: number;
}

interface EndTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWinner: (treatmentId: string) => void;
  metrics: AutomationOutreachSplitMetricsResponse;
  stepType: AutomationOutreachStepType;
  isSubmitting: boolean;
}

const EndTestModal = memo(function EndTestModal({
  isOpen,
  onClose,
  onSelectWinner,
  metrics,
  stepType,
  isSubmitting,
}: EndTestModalProps) {
  const isRecover = stepType === "recover";

  const totalTraffic = isRecover
    ? metrics.variants.reduce(
        (sum, v) => sum + (v?.checkoutAbandonments ?? 0),
        0,
      )
    : metrics.variants.reduce((sum, v) => sum + v.sends, 0);

  // Sort variants with control first, then prepare row data
  const rowsOfData = useMemo<VariantRowData[]>(() => {
    const sorted = [...metrics.variants].sort((a, b) => {
      if (a.isControl && !b.isControl) {
        return -1;
      }
      if (!a.isControl && b.isControl) {
        return 1;
      }
      return 0;
    });

    return sorted.map((variant) => {
      const variantTraffic = isRecover
        ? (variant.checkoutAbandonments ?? 0)
        : variant.sends;
      return {
        variant,
        trafficPercent:
          totalTraffic > 0
            ? ((variantTraffic / totalTraffic) * 100).toFixed(0)
            : "0",
        opens: isRecover
          ? (variant.uniqueCustomerReplies ?? 0)
          : (variant.uniqueOpens ?? 0),
        clicks: isRecover
          ? (variant.recoveredOrders ?? 0)
          : (variant.uniqueClicks ?? 0),
        revenue: isRecover
          ? (variant.recoveredRevenue ?? 0)
          : (variant.upsellRevenue ?? 0),
      };
    });
  }, [metrics.variants, totalTraffic, isRecover]);

  const columns = useMemo<RedoTableColumn<VariantRowData>[]>(() => {
    return [
      {
        key: "variant",
        widthMode: { type: "fill", relativeLength: 2 },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Variant" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={row.variant.treatmentName} />
        ),
      },
      {
        key: "traffic",
        widthMode: { type: "fill", relativeLength: 1 },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Traffic" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={`${row.trafficPercent}%`} />
        ),
      },
      {
        key: "opens",
        widthMode: { type: "fill", relativeLength: 1 },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell
            headerText={isRecover ? "Replies" : "Opens"}
          />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={String(row.opens)} />
        ),
      },
      {
        key: "clicks",
        widthMode: { type: "fill", relativeLength: 1 },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell
            headerText={isRecover ? "Recovered" : "Clicks"}
          />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={String(row.clicks)} />
        ),
      },
      {
        key: "revenue",
        widthMode: { type: "fill", relativeLength: 1 },
        renderHeaderCell: () => (
          <StandardRedoTableHeaderCell headerText="Revenue" />
        ),
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={formatCurrency(row.revenue)} />
        ),
      },
      {
        key: "action",
        widthMode: { type: "fill", relativeLength: 1 },
        alignment: "right",
        renderHeaderCell: () => null,
        renderNormalCell: ({ row }) => (
          <RedoButton
            disabled={isSubmitting || row.variant.isControl}
            hierarchy="primary"
            onClick={() => onSelectWinner(row.variant.treatmentId)}
            pending={isSubmitting}
            size="xs"
            text={row.variant.isControl ? "Control" : "Winner"}
          />
        ),
      },
    ];
  }, [isRecover, isSubmitting, onSelectWinner]);

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="lg"
      onModalCloseRequested={onClose}
      title={metrics.splitName}
    >
      <Flex dir="column" gap="xl" w="full">
        <Text fontSize="sm" textColor="secondary">
          Select a winner to complete the test. 100% of traffic will be routed
          to the winning variant.
        </Text>

        <RedoTable
          layout={{ columns, standalone: true }}
          rowsOfData={rowsOfData}
          size="sm"
        />
      </Flex>
    </RedoModal>
  );
});

export const AutomationAbTestResultsPanel = memo(
  function AutomationAbTestResultsPanel({
    automationId,
    automationStepId,
    stepType,
    title = "A/B test",
    icon,
    onSelectWinner,
    renderVariantPreview,
    renderFullVariantPreview,
    onViewVariant,
  }: AutomationAbTestResultsPanelProps) {
    const marketingRpcClient = useRequiredContext(
      RedoMarketingRpcClientContext,
    );

    const [showEndTestModal, setShowEndTestModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewingVariant, setPreviewingVariant] =
      useState<Treatment | null>(null);

    const metricsLoad = useLoad(
      async (signal) => {
        const result =
          await marketingRpcClient.getAutomationOutreachSplitMetrics(
            { automationId, automationStepId },
            { signal },
          );
        return result;
      },
      [automationId, automationStepId],
    );

    const metrics = metricsLoad.value;

    // Load full test data for variant details (includes templateId)
    const testDataLoad = useLoad(
      async (signal) => {
        const result = await marketingRpcClient.getAutomationOutreachSplit(
          { automationId, automationStepId },
          { signal },
        );
        return result;
      },
      [automationId, automationStepId],
    );

    const variants = useMemo<Treatment[]>(
      () => testDataLoad.value?.treatments ?? [],
      [testDataLoad.value],
    );

    const handleSelectWinner = useHandler(async (treatmentId: string) => {
      if (!metrics) {
        return;
      }

      setIsSubmitting(true);
      try {
        // Get the winning treatment's templateId from the full test data
        const testData = await marketingRpcClient.getAutomationOutreachSplit({
          automationId,
          automationStepId,
        });

        if (!testData) {
          throw new Error("Could not load A/B test data");
        }

        const winningTreatment = testData.treatments.find(
          (t) => t._id === treatmentId,
        );

        if (!winningTreatment?.templateId) {
          throw new Error("Winning treatment has no template");
        }

        // Stop the test
        await marketingRpcClient.stopAutomationOutreachSplit({
          splitId: metrics.splitId,
        });

        // Delete the test
        await marketingRpcClient.deleteAutomationOutreachSplit({
          splitId: metrics.splitId,
        });

        // Call the parent callback to update the step's templateId
        onSelectWinner?.(winningTreatment.templateId);

        setShowEndTestModal(false);
        toast("A/B test ended successfully", { variant: "success" });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to end test";
        toast(message, { variant: "error" });
      } finally {
        setIsSubmitting(false);
      }
    });

    if (metricsLoad.pending) {
      return (
        <InspectorLayout>
          <Flex align="center" justify="center" p="xl" w="full">
            <LoadingRedoAnimation />
          </Flex>
        </InspectorLayout>
      );
    }

    if (!metrics) {
      return (
        <InspectorLayout>
          <Flex align="center" justify="center" p="xl" w="full">
            <Text fontSize="sm" textColor="tertiary">
              No metrics available
            </Text>
          </Flex>
        </InspectorLayout>
      );
    }

    const isRecover = stepType === "recover";

    return (
      <InspectorLayout>
        {/* Header with icon and title */}
        <Flex align="center" gap="sm">
          {icon}
          <Text fontSize="md" fontWeight="semibold">
            {title}
          </Text>
        </Flex>

        {/* Test name, start date, and end test button */}
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="semibold">
            {metrics.splitName}
          </Text>
          {metrics.startDate && (
            <Text fontSize="xxs" textColor="tertiary">
              {formatDate(metrics.startDate)}
            </Text>
          )}
          <RedoButton
            hierarchy="secondary"
            onClick={() => setShowEndTestModal(true)}
            size="sm"
            text="End test"
          />
        </Flex>

        {/* Results section */}
        <InspectorSection label="Results">
          <Flex dir="column" gap="lg" w="full">
            {isRecover ? (
              <RecoverMetricsCards metrics={metrics} />
            ) : (
              <OutreachMetricsCards metrics={metrics} />
            )}
          </Flex>
        </InspectorSection>

        {/* Variants section */}
        <InspectorSection label="Variants">
          <Flex dir="column" gap="lg" w="full">
            {testDataLoad.pending ? (
              <Flex align="center" justify="center" p="md" w="full">
                <LoadingRedoAnimation />
              </Flex>
            ) : (
              variants.map((variant) => (
                <Flex dir="column" gap="sm" key={variant._id} w="full">
                  {/* Variant header with name and View button */}
                  <Flex align="center" justify="space-between" w="full">
                    <Text fontSize="sm" fontWeight="medium">
                      {variant.templateName || "[Variant name]"}
                    </Text>
                    {!variant.isControl &&
                      (onViewVariant || renderFullVariantPreview) && (
                        <RedoButton
                          hierarchy="secondary"
                          onClick={() =>
                            onViewVariant
                              ? onViewVariant(variant)
                              : setPreviewingVariant(variant)
                          }
                          size="xs"
                          text="View"
                        />
                      )}
                  </Flex>

                  {/* Variant preview */}
                  {variant.isControl ? (
                    <VariantPlaceholder type="control" />
                  ) : renderVariantPreview ? (
                    renderVariantPreview(variant)
                  ) : (
                    <VariantPlaceholder
                      hasTemplate={!!variant.templateId}
                      message={
                        variant.templateId
                          ? "Template configured"
                          : "No template selected"
                      }
                      type="template"
                    />
                  )}
                </Flex>
              ))
            )}
          </Flex>
        </InspectorSection>

        {/* End test modal */}
        <EndTestModal
          isOpen={showEndTestModal}
          isSubmitting={isSubmitting}
          metrics={metrics}
          onClose={() => setShowEndTestModal(false)}
          onSelectWinner={handleSelectWinner}
          stepType={stepType}
        />

        {/* Variant preview modal */}
        {previewingVariant && renderFullVariantPreview && (
          <RedoModal
            isOpen={!!previewingVariant}
            modalSize="lg"
            onModalCloseRequested={() => setPreviewingVariant(null)}
            secondaryButton={{
              text: "Close",
              onClick: () => setPreviewingVariant(null),
            }}
            title={previewingVariant.templateName || "Variant Preview"}
          >
            <Flex
              dir="column"
              p="lg"
              style={{ maxHeight: "70vh", overflow: "auto" }}
            >
              {renderFullVariantPreview(previewingVariant)}
            </Flex>
          </RedoModal>
        )}
      </InspectorLayout>
    );
  },
);

interface MetricsCardsProps {
  metrics: AutomationOutreachSplitMetricsResponse;
}

const OutreachMetricsCards = memo(function OutreachMetricsCards({
  metrics,
}: MetricsCardsProps) {
  const { variants } = metrics;

  const totalSends = variants.reduce((sum, v) => sum + v.sends, 0);

  return (
    <>
      <MetricCard
        primaryTitle="Traffic"
        primaryVariants={createCountMetric(variants, (v) => v.sends)}
        secondaryTitle="Traffic distribution"
        secondaryVariants={createRateMetric(
          variants,
          (v) => v.sends,
          () => totalSends,
        )}
      />
      <MetricCard
        primaryTitle="Opens"
        primaryVariants={createCountMetric(variants, (v) => v.uniqueOpens ?? 0)}
        secondaryTitle="Open rate"
        secondaryVariants={createRateMetric(
          variants,
          (v) => v.uniqueOpens ?? 0,
          (v) => v.sends,
        )}
      />
      <MetricCard
        primaryTitle="Clicks"
        primaryVariants={createCountMetric(
          variants,
          (v) => v.uniqueClicks ?? 0,
        )}
        secondaryTitle="Click rate"
        secondaryVariants={createRateMetric(
          variants,
          (v) => v.uniqueClicks ?? 0,
          (v) => v.sends,
        )}
      />
      <MetricCard
        primaryTitle="Revenue"
        primaryVariants={createCurrencyMetric(
          variants,
          (v) => v.upsellRevenue ?? 0,
        )}
      />
    </>
  );
});

/**
 * Gets the unique customers reached for a variant.
 * This counts distinct customers, not events - a customer who abandoned
 * checkout 3 times counts as 1, not 3.
 */
function getUniqueCustomersReached(v: VariantMetrics): number {
  return v.uniqueCustomersReached ?? 0;
}

/**
 * Calculates the percent increase/decrease from control to the best performing treatment.
 * Only applicable for recover experiments with a control group and N treatments.
 *
 * @param variants - The variant metrics
 * @param getValue - Function to get the value for each variant (e.g., recoveredOrders)
 */
function calculatePercentageChange<T extends VariantMetrics>(
  variants: T[],
  getValue: (v: T) => number,
): number | undefined {
  const control = variants.find((v) => v.isControl);
  const treatments = variants.filter((v) => !v.isControl);

  if (!control || treatments.length === 0) {
    return undefined;
  }

  const controlValue = getValue(control);

  // Find the best performing treatment (assumes higher is better)
  let bestTreatmentValue = 0;
  for (const treatment of treatments) {
    const treatmentValue = getValue(treatment);
    if (treatmentValue > bestTreatmentValue) {
      bestTreatmentValue = treatmentValue;
    }
  }

  // Calculate percent change
  let percentChange: number;
  if (controlValue === 0) {
    percentChange = bestTreatmentValue > 0 ? 100 : 0;
  } else {
    percentChange = ((bestTreatmentValue - controlValue) / controlValue) * 100;
  }

  return percentChange;
}

const CONVERSIONS_COUNT_TOOLTIP: RedoMetricTooltipProps = {
  title: "Conversions",
  metricDescription:
    "The number of recovered orders attributed to each variant.",
};

const REVENUE_PER_CUSTOMER_TOOLTIP: RedoMetricTooltipProps = {
  title: "Revenue per customer",
  metricDescription: "Average revenue generated per customer for each variant.",
  metricDefinition: {
    lhs: "Revenue per customer",
    rhs: [
      { type: "term", value: "recovered revenue" },
      { type: "operator", operator: "/" },
      { type: "term", value: "unique customers" },
    ] as const,
  },
};

const REPLIES_COUNT_TOOLTIP: RedoMetricTooltipProps = {
  title: "Replies",
  metricDescription:
    "The number of unique customers who replied to messages from each variant.",
};

const REPLIES_PERCENT_TOOLTIP: RedoMetricTooltipProps = {
  title: "Reply rate",
  metricDescription: "The percentage of sent messages that received a reply.",
  metricDefinition: {
    lhs: "Reply rate",
    rhs: [
      { type: "term", value: "replies" },
      { type: "operator", operator: "/" },
      { type: "term", value: "sent" },
      { type: "operator", operator: "*" },
      { type: "term", value: "100" },
    ] as const,
  },
};

const UNIQUE_CUSTOMERS_COUNT_TOOLTIP: RedoMetricTooltipProps = {
  title: "Customers",
  metricDescription: [
    "The number of unique customers reachable by each variant.",
    'There are a number of reasons why a customer is considered "unreachable". Common reasons include: already subscribed to your SMS provider, unsubscribed from Recover, completed a purchase after abandoning, or received a message within the configured cooldown period.',
  ],
};

const UNIQUE_CUSTOMERS_PERCENT_TOOLTIP: RedoMetricTooltipProps = {
  title: "Customer distribution",
  metricDescription:
    "The percentage of total unique customers allocated to each variant.",
  metricDefinition: {
    lhs: "Unique customer %",
    rhs: [
      { type: "term", value: "variant unique customers" },
      { type: "operator", operator: "/" },
      { type: "term", value: "total unique customers" },
      { type: "operator", operator: "*" },
      { type: "term", value: "100" },
    ] as const,
  },
};

const CUSTOMER_CONVERSION_RATE_TOOLTIP: RedoMetricTooltipProps = {
  title: "Conversion rate",
  metricDescription: [
    "The percentage of unique customers who placed at least one recovered order.",
  ],
  metricDefinition: {
    lhs: "Per-customer conversion rate",
    rhs: [
      { type: "term", value: "recovered orders" },
      { type: "operator", operator: "/" },
      { type: "term", value: "unique customers" },
      { type: "operator", operator: "*" },
      { type: "term", value: "100" },
    ] as const,
  },
};

const RecoverMetricsCards = memo(function RecoverMetricsCards({
  metrics,
}: MetricsCardsProps) {
  const { variants } = metrics;

  const totalUniqueCustomers = variants.reduce(
    (sum, v) => sum + getUniqueCustomersReached(v),
    0,
  );

  // Calculate percent increase for conversions (control vs best treatment)
  const conversionsCountPercentChange = calculatePercentageChange(
    variants,
    (v) => v.recoveredOrders ?? 0,
  );

  // Calculate percent increase for per-customer conversion rate (control vs best treatment)
  const customerConversionRatePercentChange = calculatePercentageChange(
    variants,
    (v) => {
      const orders = v.recoveredOrders ?? 0;
      const uniqueCustomers = getUniqueCustomersReached(v);
      return uniqueCustomers > 0 ? orders / uniqueCustomers : 0;
    },
  );

  // Calculate percent increase for revenue per customer (control vs best treatment)
  const revenuePerCustomerPercentChange = calculatePercentageChange(
    variants,
    (v) => {
      const revenue = v.recoveredRevenue ?? 0;
      const uniqueCustomers = getUniqueCustomersReached(v);
      return uniqueCustomers > 0 ? revenue / uniqueCustomers : 0;
    },
  );

  // AOV needs a custom calculation (revenue / orders)
  const aovVariants = variants.map((v) => {
    const orders = v.recoveredOrders ?? 0;
    const revenue = v.recoveredRevenue ?? 0;
    const aov = orders > 0 ? revenue / orders : 0;
    return {
      name: v.treatmentName,
      value: aov,
      formattedValue: orders > 0 ? formatCurrency(aov) : "-",
    };
  });

  // Calculate revenue per customer
  const revenuePerCustomerVariants = variants.map((v) => {
    const revenue = v.recoveredRevenue ?? 0;
    const uniqueCustomers = getUniqueCustomersReached(v);
    const value = uniqueCustomers > 0 ? revenue / uniqueCustomers : 0;
    return {
      name: v.treatmentName,
      value,
      formattedValue: uniqueCustomers > 0 ? formatCurrency(value) : "-",
    };
  });

  return (
    <>
      <MetricCard
        primaryTooltipProps={UNIQUE_CUSTOMERS_COUNT_TOOLTIP}
        primaryVariants={createCountMetric(variants, getUniqueCustomersReached)}
        secondaryTooltipProps={UNIQUE_CUSTOMERS_PERCENT_TOOLTIP}
        secondaryVariants={createRateMetric(
          variants,
          getUniqueCustomersReached,
          () => totalUniqueCustomers,
        )}
      />
      <MetricCard
        comparisonText="vs control"
        primaryPercentChange={customerConversionRatePercentChange}
        primaryTooltipProps={CUSTOMER_CONVERSION_RATE_TOOLTIP}
        primaryVariants={createRateMetric(
          variants,
          (v) => v.recoveredOrders ?? 0,
          getUniqueCustomersReached,
        )}
        secondaryPercentChange={conversionsCountPercentChange}
        secondaryTooltipProps={CONVERSIONS_COUNT_TOOLTIP}
        secondaryVariants={createCountMetric(
          variants,
          (v) => v.recoveredOrders ?? 0,
        )}
      />
      <MetricCard
        comparisonText="vs control"
        primaryPercentChange={revenuePerCustomerPercentChange}
        primaryTooltipProps={REVENUE_PER_CUSTOMER_TOOLTIP}
        primaryVariants={revenuePerCustomerVariants}
      />
      <MetricCard
        primaryTooltipProps={REPLIES_COUNT_TOOLTIP}
        primaryVariants={createCountMetric(
          variants,
          (v) => v.uniqueCustomerReplies ?? 0,
        )}
        secondaryTooltipProps={REPLIES_PERCENT_TOOLTIP}
        secondaryVariants={createRateMetric(
          variants,
          (v) => v.uniqueCustomerReplies ?? 0,
          (v) => v.initialSends ?? 0,
        )}
      />
      <MetricCard
        primaryTitle="Recovered revenue"
        primaryVariants={createCurrencyMetric(
          variants,
          (v) => v.recoveredRevenue ?? 0,
        )}
      />
      <MetricCard primaryTitle="AOV" primaryVariants={aovVariants} />
    </>
  );
});
